// byClaude-start 2026-07-01 16:30:00
import { ProductType } from '@/AppGlobal';
import type {
  ClCreditLimit,
  listSettlePayOrderDto,
  MarketingRecommendationData,
  MarketingTimeLimitData,
  PaymentDTO,
} from '@/interface/CL/clCredit';
import type { AppliedProduction } from '@/interface/pub';
import { CouponType } from '@/interface/pub';

import type { SalaryCardType } from '@/interface/KKC';
import type { CouponData, CouponIssueUserDto } from '@/interface/KKC/coupon';
import type { EddaBankInfo } from '@/interface/KKC/edda';
import type { InvitationCodeDto } from '@/interface/KKC/mgm';
import type { BankCardType, LoanEntity, repaymentPlanList } from '@/interface/KKC/withdrawal';
import { BoolString } from '@/interface/KKC/withdrawal';
import { clRequest } from '@/utils/request';
import { getGlobalUserInfo } from '@/utils/singletonUtils';
import { transformCouponData } from '@/utils/transformCouponData';
import VhkStore from '@/vhkstore';
import { setCreditApply } from '@/vhkstore/slices/KKC/creditApply';
import kkcRequest, { Options } from '@web/common/src/utils/requests/request';

/**
 * 查询EDDA绑定的卡信息
 */
export const selectCardInfo = (fillInItems?: any, options?: Options) =>
  kkcRequest.post<Array<EddaBankInfo>>('credit/binding/selectCardInfo', fillInItems, options);

/**
 * 检查银行卡号
 */
export const checkBankNum = (params: { bankCardNumEncrypt: string }) =>
  kkcRequest.post('/credit/clPay/checkBankNum', params, { hideError: true });

/**
 * 查询优惠券列表
 */
export const queryCouponList: (
  params?: {
    isLoan?: BoolString;
    loanAmt?: number;
    loanMaturity?: number;
    isOldInfo?: BoolString;
    productCode?: ProductType | string;
    orderId?: string;
    type?: string;
    billId?: string;
    divRepaymentTime?: string;
    expireFlag?: string;
    isConcertCoupon?: string;
    showUnAvailableFlag?: BoolString;
  },
  options?: Options
) => Promise<CouponData[]> = (params, options) =>
  kkcRequest
    .post<CouponIssueUserDto[]>(`/credit/coupon/selectList`, params, options, {
      productCode: params?.productCode,
    })
    .then((res) => {
      const ret: CouponData[] = [];
      res.forEach((item, idx) => {
        ret.push(transformCouponData(item, idx));
      });
      return ret;
    });

/**
 * 查询已申请的产品（CL/KKC/CMC）
 */
export const queryAppliedProduction = (option?: Options) =>
  clRequest.post<AppliedProduction>('/credit/clCredit/isApply', {}, { loading: true, ...option });

/**
 * 查询CL订单信息
 */
export const queryClOrderInfo = (params: PaymentDTO) =>
  clRequest.post<PaymentDTO>('/credit/clPay/getOrderInfo', params);

/**
 * 查询CL授信额度
 */
export const queryClCreditLimit = (productCode?: string) =>
  kkcRequest.post<ClCreditLimit>(
    '/credit/clCredit/clLimit',
    null,
    {},
    { productCode: productCode || 'CL' }
  );

// byClaude-start 2026-07-01 17:00:00
/**
 * 查询还款计划
 */
export const repaymentPlan = (
  params: {
    loanMaturity: number;
    loanAmt: number;
    paymentBankFlows: BankCardType[];
    divRepaymentTime: string;
    couponId?: string;
  },
  options?: Options
) => kkcRequest.post<repaymentPlanList>(`user/pay/repaymentPlan`, params, options);

/**
 * 查询是否可KKC现金贷
 */
export const queryCanKKCCashLoan = () => kkcRequest.post<boolean>('/credit/pay/checkKkcBankLoan');

/**
 * 查询工资日期数据
 */
export const selectSalaryData = () =>
  kkcRequest.post<SalaryCardType>('/credit/pay/selectSalaryData', {
    isPay: BoolString.YES,
  });

/**
 * 查询产品介绍信息
 */
export const queryProductIntroduceInfo = (
  cardType: string,
  productCode: string,
  layoutVersion: string
) =>
  kkcRequest.get<any>(
    `/credit/layout-card/query-product-introduce-info?cardType=${cardType}&productCode=${productCode}&layoutVersion=${layoutVersion}`
  );

// byClaude-start 2026-07-01 17:30:00
/**
 * 查询卡信息及还款期数（KKC选卡接口）
 */
export const selectCard = (params?: { loanType?: '01' | '02' }, productCode?: ProductType) =>
  kkcRequest.post<LoanEntity>(
    'user/pay/selectCard',
    params,
    {},
    productCode ? { productCode } : undefined
  );

/**
 * MGM — 验证邀请码
 */
export const selectInvitationCode = async (params: { invitationCode: string }) => {
  const userInfo = await getGlobalUserInfo();
  return kkcRequest.post<InvitationCodeDto>('/credit/apply/selectInvitationCode', {
    ...params,
    userId: userInfo.userId,
  });
};

// byClaude-start 2026-07-02 10:23:59
/**
 * 查询信用申请节点信息（KKC信用流程 — 分派到KKC store）
 */
export const selectInfo = (options?: Options) =>
  kkcRequest.post<Record<string, any>>('credit/apply/select/info', null, options).then((res) => {
    VhkStore.dispatch(setCreditApply({ creditApply: res }));
    return res;
  });

/**
 * 更新信用申请信息（KKC信用流程）
 */
export const updateCreditApply = (params: Record<string, any>) =>
  kkcRequest
    .post<Record<string, any>>('credit/apply/preserve/info', params, { loading: true })
    .then((res) => res)
    .catch((err) => {
      console.log('updateCreditApply err', err);
      throw err;
    })
    .finally(() => {
      console.log('updateCreditApply finally');
    });
// byClaude-end 2026-07-02 10:23:59
// byClaude-end 2026-07-01 17:30:00
// byClaude-end 2026-07-01 17:00:00

// byClaude-start 2026-07-02 10:23:59
/**
 * 查询清还贷款订单列表（CL/KKC/CMC共用）
 */
export const queryListSettlePayOrders = (productCode: ProductType) =>
  kkcRequest.get<listSettlePayOrderDto[]>(
    `/credit/repayment/listSettlePayOrders?productCode=${productCode}`
  );
// byClaude-end 2026-07-02 10:23:59

// byClaude-start 2026-07-02 10:23:59
/**
 * CL — 查询已选中的还款银行
 */
export const querySelectedBank = () => clRequest.post<PaymentDTO>('/credit/clPay/selectPayBank');

/**
 * CL — 查询营销时间限制
 */
export const queryMarketingTimeLimit = (params: { productLabel: ProductType }) =>
  clRequest.get<MarketingTimeLimitData>(
    `/credit/clPay/marketing/timeLimit?productLabel=${params.productLabel}`
  );

/**
 * CL — 根据营销订单ID查询推荐方案
 */
export const queryByMarketingOrderId = (params: { marketingOrderId: string }) =>
  clRequest.post<MarketingRecommendationData>(
    '/credit/clPay/marketing/queryByMarketingOrderId',
    params,
    { loading: true }
  );

/**
 * CL — 检查是否通过SLA审批
 */
export const checkHasSlaPassMuster = () =>
  clRequest.post<boolean>('/credit/clCredit/checkHasSlaPassMuster');

/**
 * CL — 查询最近一张减息券面值
 */
export const queryReduceCouponVal = () =>
  clRequest.post<{ param1: string }>('/credit/coupon/selectLastMetaInfo', {
    couponType: CouponType.ReduceInterestCoupon,
  });
// byClaude-end 2026-07-02 10:23:59
// byClaude-end 2026-07-01 16:30:00
// Contains claude-generated edits.
